import { z } from 'zod';

/** ADM-07, E-40: profile pictures. */

/** Edge length of the stored square picture (the browser crops and scales before uploading). */
export const AVATAR_SIZE_PX = 256;
/** Largest stored picture (WebP 256 × 256 stays far below). */
export const AVATAR_MAX_BYTES = 200_000;
/** Largest file the browser accepts before cropping. */
export const AVATAR_SOURCE_MAX_BYTES = 5 * 1024 * 1024;
/** Files the browser accepts before cropping; stored is always WebP. */
export const AVATAR_SOURCE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
/**
 * Stored formats: WebP where the browser can encode it, otherwise JPEG (Safari's canvas cannot
 * encode WebP). Both re-encoded in the browser, so metadata such as GPS positions is gone.
 */
export const AVATAR_CONTENT_TYPES = ['image/webp', 'image/jpeg'] as const;
export type AvatarContentType = (typeof AVATAR_CONTENT_TYPES)[number];

/**
 * Address of a profile picture, with its version so browsers may cache it for long; null without
 * a picture. Relative to the web origin (the API is served under /api).
 */
export function avatarUrl(userId: string, updatedAt: Date | string | null): string | null {
  if (updatedAt === null) return null;
  const version = (typeof updatedAt === 'string' ? new Date(updatedAt) : updatedAt).getTime();
  return `/api/v1/users/${userId}/avatar?v=${String(version)}`;
}

export const avatarProblemTypes = {
  /** Not a WebP or JPEG of AVATAR_SIZE_PX × AVATAR_SIZE_PX up to AVATAR_MAX_BYTES. */
  invalidAvatar: 'urn:pm-tool:problem:invalid-avatar',
} as const;

/** PUT /me/avatar and DELETE /me/avatar answer with the new address of the own picture. */
export const ownAvatarResponseSchema = z.object({ avatarUrl: z.string().nullable() });
export type OwnAvatarResponse = z.infer<typeof ownAvatarResponseSchema>;
